import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "@/lib/zod-lt";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useStore } from "@/store";

const NAME_FIELD = "name";
const LICENSE_NUMBER_FIELD = "license_number";
const ID_FIELD = "id";

const driverSchema = z.object({
  [NAME_FIELD]: z.string().min(2, { message: "Vardas turi būti bent 2 simbolių." }),
  [LICENSE_NUMBER_FIELD]: z
    .string()
    .min(1, { message: "Vairuotojo pažymėjimo numeris privalomas." }),
  [ID_FIELD]: z.number().optional(),
});

type DriverFormProps = {
  onDriverFormSubmit?: (driver: z.infer<typeof driverSchema>) => void;
  id?: number;
  name?: string;
  license_number?: string;
};

export function DriverForm({
  onDriverFormSubmit,
  ...loaderData
}: DriverFormProps): React.JSX.Element {
  const { createDriver, updateDriver, isCreateDriverLoading, isUpdateDriverLoading } = useStore(
    (s) => s,
  );

  const form = useForm<z.infer<typeof driverSchema>>({
    mode: "all",
    resolver: zodResolver(driverSchema),
    defaultValues: {
      [NAME_FIELD]: loaderData?.name ?? "",
      [LICENSE_NUMBER_FIELD]: loaderData?.license_number ?? "",
      [ID_FIELD]: loaderData?.id,
    },
  });

  if (Object.keys(form.formState.errors).length > 0) {
    console.debug(form.formState.errors);
  }

  async function onSubmit(formData: z.infer<typeof driverSchema>): Promise<void> {
    if (loaderData?.id) {
      const updatedDriver = await updateDriver(loaderData.id, {
        name: formData[NAME_FIELD],
        license_number: formData[LICENSE_NUMBER_FIELD],
      });
      onDriverFormSubmit?.({
        name: updatedDriver.name,
        license_number: updatedDriver.license_number,
        id: updatedDriver.id,
      });
    } else {
      const newDriver = await createDriver({
        name: formData[NAME_FIELD],
        license_number: formData[LICENSE_NUMBER_FIELD],
      });
      onDriverFormSubmit?.({
        name: newDriver.name,
        license_number: newDriver.license_number,
        id: newDriver.id,
      });
    }
  }

  const buttonIdleLabel = loaderData?.id ? "Atnaujinti" : "Sukurti";
  const buttonLoadingLabel = loaderData?.id ? "Atnaujinama..." : "Kuriama...";
  const isLoading = isCreateDriverLoading || isUpdateDriverLoading;
  const buttonLabel = isLoading ? buttonLoadingLabel : buttonIdleLabel;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <FormField
            control={form.control}
            name={NAME_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vardas, pavardė*</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormDescription>Vairuotojo vardas ir pavardė</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={LICENSE_NUMBER_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vairuotojo pažymėjimo numeris*</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormDescription>Vairuotojo pažymėjimo numeris</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.getValues(ID_FIELD) && (
            <FormField
              control={form.control}
              name={ID_FIELD}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="hidden" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
          {buttonLabel}
        </Button>
      </form>
    </Form>
  );
}
